import { useState } from "react";
import { Button, Group, Loader, Select, Stack, Text } from "@mantine/core";
import { modals } from "@mantine/modals";
import dayjs from "dayjs";
import { isContentEmpty } from "@congrs/utils";
import { useGetAllCampaigns } from "../../api/campaigns";
import { useSendCampaignToTicket } from "../../api/campaigns";

const formatDate = (date) => dayjs(date).format("DD.MM.YYYY. [at] HH:mm");

const statusColors = {
  draft: "gray",
  scheduled: "yellow",
  sent: "green",
};

const getCampaignStatus = (campaign) => {
  if (campaign.status === "scheduled") {
    return { word: "Scheduled", timestamp: formatDate(campaign.scheduledAt) };
  }
  if (campaign.status === "sent") {
    return { word: "Sent", timestamp: formatDate(campaign.sentAt) };
  }
  return { word: "Draft", timestamp: null };
};

const SendCampaign = ({ eventId, ticket, onClose }) => {
  const [selectedCampaignId, setSelectedCampaignId] = useState(null);

  const { data, isPending } = useGetAllCampaigns({
    eventId,
    page: 1,
    limit: 100,
  });

  const { mutate: sendCampaign, isPending: isSending } =
    useSendCampaignToTicket({
      eventId,
      ticketId: ticket.id,
      campaignId: selectedCampaignId,
    });

  if (isPending) {
    return <Loader />;
  }

  const campaigns = (data?.data ?? []).filter(
    (c) => !isContentEmpty(c.contentHtml)
  );

  const campaignsById = Object.fromEntries(
    campaigns.map((campaign) => [String(campaign.id), campaign])
  );

  const handleSend = () => {
    sendCampaign(
      {},
      {
        onSuccess: () => onClose(),
      }
    );
  };

  return (
    <Stack>
      <Text size="sm">
        Select a campaign to send to{" "}
        <strong>
          {ticket.firstName} {ticket.lastName}
        </strong>{" "}
        ({ticket.email}).
      </Text>

      {campaigns.length === 0 ? (
        <Text size="sm" c="dimmed" ta="center" py="md">
          No campaigns available for this event.
        </Text>
      ) : (
        <Select
          placeholder="Select a campaign"
          value={selectedCampaignId ? String(selectedCampaignId) : null}
          onChange={setSelectedCampaignId}
          data={campaigns.map((campaign) => {
            const status = getCampaignStatus(campaign);
            return {
              value: String(campaign.id),
              label: `${campaign.subject} — ${status.word}${
                status.timestamp ? ` ${status.timestamp}` : ""
              }`,
            };
          })}
          renderOption={({ option }) => {
            const campaign = campaignsById[option.value];
            const status = getCampaignStatus(campaign);
            return (
              <Text size="sm">
                {campaign.subject} —{" "}
                <Text span c={statusColors[campaign.status]} fw={600}>
                  {status.word}
                </Text>
                {status.timestamp ? ` ${status.timestamp}` : ""}
              </Text>
            );
          }}
          searchable
          nothingFoundMessage="No campaigns match"
        />
      )}

      <Group justify="flex-end" mt="md">
        <Button variant="default" onClick={onClose}>
          Cancel
        </Button>
        <Button
          color="blue"
          disabled={!selectedCampaignId || isSending}
          loading={isSending}
          onClick={handleSend}
        >
          Send
        </Button>
      </Group>
    </Stack>
  );
};

export const openSendCampaignModal = ({ eventId, ticket }) => {
  modals.open({
    title: "Send campaign",
    children: (
      <SendCampaign
        eventId={eventId}
        ticket={ticket}
        onClose={() => modals.closeAll()}
      />
    ),
  });
};

export default SendCampaign;
